import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { adminFetch, fmt } from "@/shop/api";

export default function AdminConfig() {
  const [cfg, setCfg] = useState({ free_shipping_threshold: 0, shipping_zones: [] });
  const [brands, setBrands] = useState([]);
  const [newBrand, setNewBrand] = useState("");
  const [zone, setZone] = useState({ name: "", countries: "IT", rate: 0 });

  const load = () => {
    adminFetch("/api/admin/config").then((r) => r.json()).then(setCfg).catch(() => {});
    adminFetch("/api/admin/brands").then((r) => r.json()).then(setBrands).catch(() => {});
  };
  useEffect(() => {
    load();
  }, []);

  const saveThreshold = async (value) => {
    const res = await adminFetch("/api/admin/config", {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ free_shipping_threshold: parseFloat(value) || 0 }),
    }).then((r) => r.json());
    setCfg(res);
    toast.success("Soglia salvata");
  };

  const saveZones = async (zones) => {
    const res = await adminFetch("/api/admin/config", {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ shipping_zones: zones }),
    }).then((r) => r.json());
    setCfg(res);
    toast.success("Zone di spedizione salvate");
  };

  const addZone = () => {
    if (!zone.name.trim()) return toast.error("Dai un nome alla zona");
    const zones = [
      ...(cfg.shipping_zones || []),
      {
        name: zone.name,
        countries: zone.countries.split(",").map((c) => c.trim()).filter(Boolean),
        rate: parseFloat(zone.rate) || 0,
        active: true,
      },
    ];
    setZone({ name: "", countries: "IT", rate: 0 });
    saveZones(zones);
  };

  const toggleZone = (idx) => {
    const zones = cfg.shipping_zones.map((z, i) =>
      i === idx ? { ...z, active: !z.active } : z,
    );
    saveZones(zones);
  };

  const removeZone = (idx) => saveZones(cfg.shipping_zones.filter((_, i) => i !== idx));

  const addBrand = async () => {
    if (!newBrand.trim()) return;
    await adminFetch("/api/admin/brands", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name: newBrand, active: true, sort: brands.length + 1 }),
    });
    setNewBrand("");
    toast.success("Marca aggiunta");
    load();
  };

  return (
    <div className="max-w-3xl space-y-12" data-testid="admin-config">
      <section>
        <h2 className="font-mono text-[10px] uppercase tracking-[0.3em] text-faint">Soglia spedizione gratuita</h2>
        <div className="mt-4 flex items-center gap-4">
          <input
            data-testid="config-threshold"
            type="number"
            step="0.01"
            min="0"
            defaultValue={cfg.free_shipping_threshold}
            onBlur={(e) =>
              parseFloat(e.target.value) !== cfg.free_shipping_threshold && saveThreshold(e.target.value)
            }
            className="w-40 bg-ink border border-ink-border rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-ember"
          />
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-faint">
            attuale: {fmt(cfg.free_shipping_threshold)} — salva uscendo dal campo
          </span>
        </div>
      </section>

      <section>
        <h2 className="font-mono text-[10px] uppercase tracking-[0.3em] text-faint">
          Zone e tariffe di spedizione
        </h2>
        <p className="mt-2 text-xs text-mist">
          Lascia la lista vuota per mostrare "calcolata dopo la conferma" al checkout.
        </p>
        <div className="mt-5 space-y-2">
          {(cfg.shipping_zones || []).map((z, i) => (
            <div key={i} data-testid={`zone-row-${i}`} className="flex flex-wrap items-center gap-3 border border-ink-border bg-ink-surface px-4 py-3">
              <span className="font-serif text-sm flex-1">{z.name}</span>
              <span className="font-mono text-[10px] text-faint">{(z.countries || []).join(", ")}</span>
              <span className="font-mono text-xs">{fmt(z.rate)}</span>
              <button
                data-testid={`zone-toggle-${i}`}
                onClick={() => toggleZone(i)}
                className={`font-mono text-[9px] uppercase tracking-[0.2em] px-3 py-1.5 rounded-full border ${z.active ? "border-emerald-700 text-emerald-400" : "border-faint text-faint"}`}
              >
                {z.active ? "Attiva" : "Spenta"}
              </button>
              <button data-testid={`zone-remove-${i}`} onClick={() => removeZone(i)} className="text-faint hover:text-ember">
                <Trash2 size={14} strokeWidth={1.5} />
              </button>
            </div>
          ))}
        </div>
        <div className="mt-4 flex flex-wrap gap-3 items-end">
          <div>
            <label className="block font-mono text-[9px] uppercase tracking-[0.2em] text-faint mb-1.5">Nome zona</label>
            <input data-testid="zone-name" placeholder="Italia" value={zone.name} onChange={(e) => setZone({ ...zone, name: e.target.value })} className="w-44 bg-ink border border-ink-border rounded-lg px-3 py-2.5 text-sm" />
          </div>
          <div>
            <label className="block font-mono text-[9px] uppercase tracking-[0.2em] text-faint mb-1.5">Paesi (sigle, virgole)</label>
            <input data-testid="zone-countries" placeholder="IT oppure IT,FR,DE" value={zone.countries} onChange={(e) => setZone({ ...zone, countries: e.target.value })} className="w-48 bg-ink border border-ink-border rounded-lg px-3 py-2.5 text-sm" />
          </div>
          <div>
            <label className="block font-mono text-[9px] uppercase tracking-[0.2em] text-faint mb-1.5">Tariffa €</label>
            <input data-testid="zone-rate" type="number" step="0.01" value={zone.rate} onChange={(e) => setZone({ ...zone, rate: e.target.value })} className="w-28 bg-ink border border-ink-border rounded-lg px-3 py-2.5 text-sm" />
          </div>
          <button data-testid="zone-add" onClick={addZone} className="inline-flex items-center gap-2 bg-ember text-ink font-mono text-[10px] uppercase tracking-[0.25em] px-5 py-2.5 rounded-full hover:bg-ember-bright">
            <Plus size={13} /> Aggiungi zona
          </button>
        </div>
      </section>

      <section>
        <h2 className="font-mono text-[10px] uppercase tracking-[0.3em] text-faint">Marchi (Selected Tackle)</h2>
        <p className="mt-2 text-xs text-mist">
          I marchi compaiono nei filtri solo quando hanno almeno un prodotto online.
        </p>
        <div className="mt-5 flex flex-wrap gap-2">
          {brands.map((b) => (
            <span key={b.slug} className="font-mono text-[10px] uppercase tracking-[0.2em] border border-ink-border rounded-full px-4 py-2 text-mist" data-testid={`brand-chip-${b.slug}`}>
              {b.name}
            </span>
          ))}
        </div>
        <div className="mt-4 flex gap-3">
          <input
            data-testid="brand-new"
            placeholder="Nome marca (es. Gardner)"
            value={newBrand}
            onChange={(e) => setNewBrand(e.target.value)}
            className="w-64 bg-ink border border-ink-border rounded-lg px-3 py-2.5 text-sm"
          />
          <button data-testid="brand-add" onClick={addBrand} className="bg-ember text-ink font-mono text-[10px] uppercase tracking-[0.25em] px-5 py-2.5 rounded-full hover:bg-ember-bright">
            Aggiungi
          </button>
        </div>
      </section>
    </div>
  );
}
